import { z } from "zod";
import type { Industry } from "./industry.js";
import type { Product } from "./product.js";

// ---------------------------------------------------------------------------
// 使う機能（B、#142 段3、docs/52 §3）。会社ごとの9つのスイッチ（設定 → 使う機能、管理者だけ変える）。
//   ・A（モジュール tenant_modules）は「その製品にあるか」＝API が 404 で止める。B は「その会社が使うか」＝画面で隠すだけ
//   ・OFF にしても入っている値で紙を刷り、金額を計算する（データは残す。Board の機能表示設定と同じ考え）
//   ・既定は 業種 → 製品 で決まる：現場力は全部 ON（見積依頼 rfq だけ 電気・塗装・設備で OFF）、営業力は trades・items だけ ON。
//     業種の無い今の会社は製品の既定（現場力は rfq も ON）
//   ・rfq（見積依頼、#149）は vendorQuote（外注見積の取込）と関係なく使える（rfq だけ ON なら返事は「回答ありにする」で付ける）
//   ・receivedDocs（受け取った書類、../eigyouryoku/docs/13）は両製品とも既定 ON（{project}の画面の枠。読み取りは別のモジュール doc_read＝2回目）
//   ・reducedTax（軽減税率・税区分、T2・../eigyouryoku/docs/14）は業種 食品・物販で既定 ON、ほかの業種と業種の無い会社は OFF（両製品）。
//     ON＝明細の表に「税区分」の列。OFF でも行の詳細（2段目）の「税区分」から選べる（立替金・非課税だけ使う会社のため）。データと紙は同じ
//   ・DB（tenants.feature_overrides）は会社が変えた分だけ。解決はここ（resolveUses）1か所、画面の判定は apps/web/src/core/uses.ts 1か所
// ---------------------------------------------------------------------------

// #266：deliveryNote（納品書）＝10 個目・acceptance（検収書）＝11 個目（1b。既定は両製品ともオフ）
// #289：ongoingProjects（継続案件）＝12 個目（既定は両製品ともオフ。業種で ON＝卸売・物販・IT・士業・保守）
export const USE_KEYS = ["trades", "places", "items", "takeoff", "vendorQuote", "rfq", "map", "receivedDocs", "reducedTax", "deliveryNote", "acceptance", "ongoingProjects"] as const;
export const UseKey = z.enum(USE_KEYS);
export type UseKey = z.infer<typeof UseKey>;

/** 会社が変えた分（{"places":false} など。DB の CHECK と同じ形） */
export const FeatureOverrides = z.partialRecord(UseKey, z.boolean());
export type FeatureOverrides = z.infer<typeof FeatureOverrides>;

/** 製品の既定（業種の無い会社はこれ） */
export const USE_DEFAULTS: Record<Product, readonly UseKey[]> = {
  // reducedTax（T2）は業種で ON にするだけ（業種の無い会社は OFF）。納品書（#266）は現場力の既定オフ（工務店は見積 → 請求で回る）
  genba: USE_KEYS.filter((k) => k !== "reducedTax" && k !== "deliveryNote" && k !== "acceptance" && k !== "ongoingProjects"),
  eigyo: ["trades", "items", "receivedDocs", "deliveryNote"],
};

/**
 * 業種で製品の既定から外すもの（#149）。見積依頼は工務店（と業種の無い今の会社）だけ既定で ON
 * （電気・塗装・設備は自社で施工することが多く、外に見積を頼む場面が少ない＝使うときに ON にする）
 */
export const USE_INDUSTRY_OFF: Partial<Record<Industry, readonly UseKey[]>> = {
  electric: ["rfq"],
  painting: ["rfq"],
  equipment: ["rfq"],
  // #289：卸売は品物を売る会社＝工事の区分・場所・{takeoff}・{vendorQuote}・見積依頼・地図は使わない（docs/101 §8.1）
  wholesale: ["trades", "places", "takeoff", "vendorQuote", "rfq", "map"],
};

/** 業種で製品の既定に足すもの（T2）。軽減税率・税区分は食品・物販（飲食料品・新聞を扱う会社）で既定 ON */
export const USE_INDUSTRY_ON: Partial<Record<Industry, readonly UseKey[]>> = {
  food: ["reducedTax"],
  // #289（docs/101 §8.1）：固定の得意先に毎月売る業種は継続案件。卸売は{deliveryNote}も
  retail: ["reducedTax", "ongoingProjects"],
  wholesale: ["deliveryNote", "ongoingProjects"],
  it: ["ongoingProjects"],
  consulting: ["ongoingProjects"],
  maintenance: ["ongoingProjects"],
};

/** 既定＝製品の既定から業種で外すものを外し、業種で足すものを足す。並びは USE_KEYS の順 */
export function defaultUses(product: Product, industry: Industry | null | undefined): readonly UseKey[] {
  const off = (industry && USE_INDUSTRY_OFF[industry]) || [];
  const on = (industry && USE_INDUSTRY_ON[industry]) || [];
  return USE_KEYS.filter((k) => (USE_DEFAULTS[product].includes(k) && !off.includes(k)) || on.includes(k));
}

/** いま使う機能＝既定（業種 → 製品）→ 会社が変えた分。並びは USE_KEYS の順 */
export function resolveUses(product: Product, industry: Industry | null | undefined, overrides: unknown): UseKey[] {
  const on = new Set<UseKey>(defaultUses(product, industry));
  const o = FeatureOverrides.safeParse(overrides);
  if (o.success) {
    for (const k of USE_KEYS) {
      if (o.data[k] === true) on.add(k);
      if (o.data[k] === false) on.delete(k);
    }
  }
  return USE_KEYS.filter((k) => on.has(k));
}

/** GET /company/features・PUT の答え */
export const FeatureSettings = z.object({
  /** いま ON のもの（既定＋会社が変えた分） */
  uses: z.array(UseKey),
  /** 既定（業種 → 製品）。設定の画面で「既定」と見せる */
  defaults: z.array(UseKey),
  /** 会社が変えた分 */
  overrides: FeatureOverrides,
});
export type FeatureSettings = z.infer<typeof FeatureSettings>;

/** PUT /company/features（管理者）。1つずつ（設定の画面のスイッチは押した瞬間に保存）。既定と同じなら「変えた分」から外す */
export const FeatureUpdate = z.object({ key: UseKey, on: z.boolean() });
export type FeatureUpdate = z.infer<typeof FeatureUpdate>;

/** 設定の画面の名前と「？」（{trade} などは言葉を通す） */
export const USE_INFO: Record<UseKey, { title: string; help: string }> = {
  trades: { title: "{trade}", help: "行の詳細の{trade}、表示順の「{trade}」、設定の{trade}、スマホのまとめ方を出します。" },
  places: { title: "場所", help: "行の詳細の場所、表示順の「場所」、設定の場所、スマホのまとめ方を出します。" },
  items: { title: "品目マスタ", help: "設定の「品目」のタブを出します（よく使う品目と既定の単位）。" },
  takeoff: { title: "{takeoff}", help: "数量の欄の{takeoff}（数量の根拠）と、{takeoffSheet}を出します。" },
  vendorQuote: { title: "{vendorQuote}の取込", help: "{project}の「{vendorQuote}」のタブ、見積の{vendorQuote}の帯、行の「取込」を出します。" },
  // #149：見積依頼は{vendorQuote}と関係なく使える（7つ目のスイッチ）
  rfq: { title: "{vendor}への見積依頼", help: "見積の「≡」の「見積依頼書作成」、行の「見積依頼に追加」、ホームの「{vendor}の未回答」、設定の「見積依頼」を出します。送った依頼は消えません。" },
  map: { title: "地図", help: "{project}の住所の下の地図を出します（住所は残ります）。" },
  // 受け取った書類（../eigyouryoku/docs/13 §4.4）：両製品とも既定 ON
  receivedDocs: { title: "受け取った書類", help: "{project}の画面に「受け取った書類」の枠を出します（注文書・見積依頼・図面・写真・資料の保管）。" },
  // 軽減税率・税区分（T2、../eigyouryoku/docs/14）：食品・物販で既定 ON
  reducedTax: { title: "軽減税率・税区分", help: "明細ごとに税区分（軽減・非課税・立替金など）を選べるようにします。食品・新聞を扱う会社向け" },
  // 納品書（#266、docs/96）：営業力は既定オン・現場力は既定オフ
  deliveryNote: { title: "{deliveryNote}", help: "左のメニューの「{deliveryNote}」、受注した見積の「{deliveryNote}作成」、{project}のタブ、ホームの「未納品」を出します。" },
  // 検収書（#266 1b）：両製品とも既定オフ。{customer}・{deliveryNote}・請求書のスイッチ「{acceptance}」もこれがオンのときだけ
  acceptance: { title: "{acceptance}", help: "{deliveryNote}の一覧のタブ「{acceptance}」、{deliveryNote}・請求書の「{acceptance}」のスイッチ、{customer}の「{acceptance}」、売上の基準「検収日」を出します。" },
  // 継続案件（#289、docs/101）：卸売・物販・IT・士業・保守で既定オン
  ongoingProjects: { title: "{ongoingProject}", help: "固定の{customer}に見積書・{deliveryNote}・請求書を積む{project}です。作成画面の{project}の既定になり、{project}の一覧のタブと{project}の月別の表が出ます。" },
};

export const FEATURES_MSG = {
  title: "使う機能",
  help: "「使わない」にすると、その機能のボタンや欄が画面から隠れます。入っているデータは消えず、見積の紙・金額も変わりません。「使う」に戻すと元どおり出ます。",
  on: "使う",
  off: "使わない",
  saved: "「{name}」を「{state}」にしました",
  isDefault: "既定",
} as const;
